import React from 'react';
import { VibeField } from '../profile/fields';
import { HERE_TO_FIND, LOOKING_EXAMPLES, LOOKING_MAX, LOVE_CHIP, OCCUPATION_MAX, VIBE_MAX, socialHandle } from '../../utils/portalPageModel';
import { INVITE_CAP, step2Missing } from '../../utils/onboardingModel';
import { maskNumber } from '../../utils/contactMatch';
import ContactPickList from './ContactPickList';
import {
  CYAN, CheckBox, CheckDisc, HELP, Icon, InitialsDisc, LABEL, MiniPill, PINK, PillField, SectionCard, SectionHead, irisRing, pill,
} from './onboardingUi';

/*
 * Sign-up v3 — the four steps' bodies (design_handoff_signup_v3, Oct 22 2026; v3.1 Oct 30). The NAME and
 * VIBE CHECK fields are the shared Portal field kit's, so what a host reads on a Portal Page was
 * typed into the same control whether it came from here or from `/portal/edit`.
 */

const PEOPLE_PLACEHOLDERS = 12;

/** A handle pill: the glyph, a fixed `@`, then a bare input; a leading `@` is stripped as it is typed. */
function HandlePill({ icon, value, onChange, placeholder, label, testId }) {
  return (
    <span className="pe-in-wrap" style={{ display: 'flex', alignItems: 'center', gap: 6, height: 52, padding: '0 14px', borderRadius: 999, background: '#000', boxShadow: 'inset 0 0 0 1px #2A2A2A', minWidth: 0 }}>
      {icon}
      <span aria-hidden="true" style={{ fontSize: 15, color: '#6B6B6B' }}>@</span>
      <input
        value={value}
        // No maxLength: a pasted profile URL is longer than a handle and the browser would cut it
        // before `socialHandle` could read the handle out of it. The cleaner caps at SOCIAL_HANDLE_MAX.
        onChange={(e) => onChange(socialHandle(e.target.value))}
        placeholder={placeholder}
        aria-label={label}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        data-testid={testId}
        style={{ flex: 1, minWidth: 0, height: '100%', border: 0, background: 'none', boxShadow: 'none', outline: 'none', fontFamily: 'inherit', fontSize: 15, color: '#fff' }}
      />
    </span>
  );
}

const SUBLABEL = { display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 10, fontWeight: 800, letterSpacing: '.16em', color: '#BDBDBD' };

/**
 * The YOU card (v3.1) — first on KNOW ANYONE HERE?. YOUR NAME is the only required field on the
 * screen: the friend requests this step sends and the invite text on the next one carry it. WHAT
 * YOU DO and the two handles are optional and land on the same fields THE BASICS edits. After a
 * refused SYNC CONTACTS the card's ring and the meta go pink.
 */
export function YouStrip({ you, onChange, tried }) {
  const nameOk = step2Missing(you).length === 0;
  const missing = tried && !nameOk;
  return (
    <section data-testid="you-strip" style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '20px 22px 22px', borderRadius: 28, background: '#111', boxShadow: `inset 0 0 0 1px ${missing ? PINK : '#1F1F1F'}`, transition: 'box-shadow .2s', minWidth: 0 }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 11, fontWeight: 800, letterSpacing: '.16em' }}>
          YOUR NAME
          <span data-testid="you-meta" style={{ color: nameOk ? CYAN : missing ? PINK : '#6B6B6B', whiteSpace: 'nowrap', flexShrink: 0, transition: 'color .2s' }}>{nameOk ? '✓ SET' : 'REQUIRED'}</span>
        </span>
        <input
          value={you.name}
          onChange={(e) => onChange({ name: e.target.value })}
          maxLength={60}
          placeholder="What people call you"
          autoComplete="name"
          className="pe-in"
          data-testid="you-name"
          style={{ height: 60, padding: '0 24px', border: 0, borderRadius: 999, background: '#000', boxShadow: `inset 0 0 0 1px ${missing ? PINK : '#2A2A2A'}`, fontFamily: 'inherit', fontSize: 18, fontWeight: 700, color: '#fff', width: '100%', boxSizing: 'border-box', outline: 'none' }}
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={SUBLABEL}>WHAT YOU DO <span style={{ color: '#6B6B6B' }}>OPTIONAL</span></span>
        <input
          value={you.job}
          onChange={(e) => onChange({ job: e.target.value.slice(0, OCCUPATION_MAX) })}
          maxLength={OCCUPATION_MAX}
          placeholder="Winemaker, nurse, DJ, founder"
          autoComplete="organization-title"
          className="pe-in"
          data-testid="you-job"
          style={{ height: 52, padding: '0 22px', border: 0, borderRadius: 999, background: '#000', boxShadow: 'inset 0 0 0 1px #2A2A2A', fontFamily: 'inherit', fontSize: 15, color: '#fff', width: '100%', boxSizing: 'border-box', outline: 'none' }}
        />
      </label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={SUBLABEL}>WHERE ELSE TO FIND YOU <span style={{ color: '#6B6B6B' }}>OPTIONAL</span></span>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
          <HandlePill icon={Icon.ig} value={you.ig} onChange={(ig) => onChange({ ig })} placeholder="instagram" label="Instagram" testId="you-ig" />
          <HandlePill icon={Icon.tt} value={you.tt} onChange={(tt) => onChange({ tt })} placeholder="tiktok" label="TikTok" testId="you-tt" />
        </div>
      </div>
      <span style={{ fontSize: 11, lineHeight: 1.5, color: '#6B6B6B' }}>Your name goes on friend requests and invite texts.</span>
    </section>
  );
}

/** STEP 1 · KNOW ANYONE HERE? — the YOU card, then a small grid of faces from the town; SYNC CONTACTS below. */
export function StepKnowAnyone({ people, loading, tapped, onToggle, you, onYou, tried, city, cols = 4 }) {
  const tiles = loading && !people.length ? Array.from({ length: PEOPLE_PLACEHOLDERS }, (_, i) => ({ userId: `ph${i}`, placeholder: true })) : people;
  return (
    <>
      <YouStrip you={you} onChange={onYou} tried={tried} />
      {tiles.length > 0 ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '10px 4px 0', fontSize: 10, fontWeight: 800, letterSpacing: '.16em', color: '#8A8A8A' }}>
            <span style={{ minWidth: 0 }}>PEOPLE IN {(city || '').toUpperCase()}</span>
            <span style={{ color: '#6B6B6B', flexShrink: 0 }}>TAP WHO YOU KNOW</span>
          </div>
          <div data-testid="know-grid" style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))`, gap: 8 }}>
            {tiles.map((p) => {
              const on = tapped.has(p.userId);
              if (p.placeholder) return <span key={p.userId} aria-hidden="true" style={{ display: 'block', aspectRatio: '1 / 1.36', borderRadius: 18, background: '#0B0B0B', boxShadow: 'inset 0 0 0 1px #1F1F1F' }} />;
              return (
                <button
                  key={p.userId}
                  type="button"
                  onClick={() => onToggle(p)}
                  aria-pressed={on}
                  data-testid="know-tile"
                  style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '5px 5px 8px', border: 0, borderRadius: 18, background: on ? '#111' : '#0B0B0B', boxShadow: `inset 0 0 0 ${on ? 2 : 1}px ${on ? CYAN : '#1F1F1F'}`, color: '#fff', fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer', transition: 'box-shadow .2s, background-color .2s', minWidth: 0, WebkitTapHighlightColor: 'transparent' }}
                >
                  <span style={{ position: 'relative', display: 'block', aspectRatio: '1', borderRadius: 14, overflow: 'hidden', background: '#1A1A1A' }}>
                    {p.avatarUrl
                      ? <img src={p.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      : <span style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800, color: '#3A3A3A' }}>{(p.displayName || '?').trim().charAt(0).toUpperCase()}</span>}
                    {on && <CheckDisc size={20} style={{ position: 'absolute', right: 4, top: 4 }} />}
                  </span>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '0 4px', minWidth: 0 }}>
                    <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.1em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(p.displayName || '').split(' ')[0].toUpperCase()}</span>
                    <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: '.08em', color: '#6B6B6B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minHeight: 10 }}>{p.tag || ''}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <SectionCard gap={10} data-testid="know-empty">
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.16em' }}>NOBODY HERE YET</span>
          <span style={HELP}>You are early. Sync your contacts to find who you already know, and your face will be the first one the next person sees.</span>
        </SectionCard>
      )}
      <SectionCard gap={8} style={{ padding: '18px 22px' }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.16em' }}>SYNC CONTACTS</span>
        <span style={HELP}>Finds everyone in your phone who is already here. Nothing is sent without you.</span>
      </SectionCard>
    </>
  );
}

/** The 52px face in card A's row: ring #2A2A2A, cyan once added. */
function MatchFace({ m, added }) {
  return (
    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, width: 56 }}>
      <span style={{ width: 52, height: 52, borderRadius: '50%', overflow: 'hidden', background: '#1A1A1A', boxShadow: `0 0 0 2px ${added ? CYAN : '#2A2A2A'}`, transition: 'box-shadow .2s', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {m.user.avatarUrl
          ? <img src={m.user.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          : <span style={{ fontSize: 16, fontWeight: 800, color: '#6B6B6B' }}>{(m.user.displayName || '?').charAt(0).toUpperCase()}</span>}
      </span>
      <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.1em', color: '#BDBDBD', maxWidth: 56, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(m.user.displayName || '').split(' ')[0].toUpperCase()}</span>
    </span>
  );
}

/**
 * STEP 2 · CONTACTS — card A: the matches and ADD ALL; card B: WHO SHOULD BE HERE · PICK up to INVITE_CAP from the
 * rest of the book (or a typed number on a laptop / when the book is closed to us).
 */
export function StepContacts({ contacts, addedAll, onAddAll, invites, onToggleInvite, query, onQuery, typed, onTyped, onAddTyped, typedRows, onRetry }) {
  const { status, source, matches = [], others = [], error } = contacts;
  const addable = matches.filter((m) => m.user.relationshipStatus === 'none' || m.user.relationshipStatus === 'request_received');
  const n = matches.length;
  const added = addedAll || (n > 0 && addable.length === 0);
  const canList = (source === 'native' || source === 'picker') && status === 'done';
  const rows = canList ? others : [];
  const pickedCount = invites.size;

  return (
    <>
      {status === 'loading' && (
        <SectionCard gap={12} data-testid="contacts-loading">
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.16em' }}>READING YOUR CONTACTS…</span>
          <span style={HELP}>Checking who is already here. Your contacts stay on your phone; only a fingerprint of each number is compared.</span>
        </SectionCard>
      )}
      {status === 'denied' && (
        <SectionCard gap={12} ring="#5A1A3E" data-testid="contacts-denied">
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.16em', color: PINK }}>CONTACTS ARE OFF FOR PORTAL</span>
          <span style={HELP}>Turn them on in Settings and come back, or type a number below. Nothing here is required.</span>
          {onRetry && <MiniPill onClick={onRetry} data-testid="contacts-retry">TRY AGAIN</MiniPill>}
        </SectionCard>
      )}
      {status === 'error' && (
        <SectionCard gap={12} ring="#5A1A3E" data-testid="contacts-error">
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.16em', color: PINK }}>COULDN’T CHECK YOUR CONTACTS</span>
          <span style={HELP}>{error || 'Check your connection and try again, or type a number below.'}</span>
          {onRetry && <MiniPill onClick={onRetry} data-testid="contacts-retry">TRY AGAIN</MiniPill>}
        </SectionCard>
      )}
      {status === 'done' && n > 0 && (
        <SectionCard gap={16} data-testid="contacts-matches">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 8px' }}>
            {matches.map((m) => <MatchFace key={m.user.userId} m={m} added={added || m.user.relationshipStatus === 'friends' || m.user.relationshipStatus === 'request_sent'} />)}
          </div>
          <button
            type="button"
            onClick={added ? undefined : onAddAll}
            data-testid="add-all"
            aria-pressed={added}
            className={`pk-btn ${added ? '' : 'pk-primary-on-black'}`}
            style={{ height: 50, width: '100%', background: added ? '#111' : '#fff', color: added ? CYAN : '#000', fontSize: 11, fontWeight: 800, letterSpacing: '.16em', boxShadow: added ? 'inset 0 0 0 1px #1F1F1F' : 'none', cursor: added ? 'default' : 'pointer' }}
          >
            {added ? `✓ ${n} ADDED AS FRIENDS` : `ADD ALL ${n} AS FRIENDS`}
          </button>
        </SectionCard>
      )}
      {status === 'done' && n === 0 && source !== 'none' && (
        <SectionCard gap={10} data-testid="contacts-none">
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.16em' }}>NOBODY FROM YOUR PHONE IS HERE YET</span>
          <span style={HELP}>{contacts.received ? `${contacts.received} checked. ` : ''}You get to bring the first ones.</span>
        </SectionCard>
      )}

      <SectionCard gap={14} data-testid="contacts-invite">
        <SectionHead title="WHO SHOULD BE HERE" right={<span style={{ color: pickedCount ? CYAN : '#6B6B6B', whiteSpace: 'nowrap' }}>{pickedCount ? `${pickedCount} OF ${INVITE_CAP}` : `PICK ${INVITE_CAP}`}</span>} />
        <span style={HELP}>{rows.length ? 'From your phone. They get one text from you, with your name on it.' : 'They get one text from you, with your name on it.'}</span>
        {rows.length > 0 && (
          <ContactPickList rows={rows} picked={invites} onToggle={onToggleInvite} cap={INVITE_CAP} query={query} onQuery={onQuery} />
        )}
        {typedRows.length > 0 && (
          <div data-testid="typed-rows" style={{ display: 'flex', flexDirection: 'column' }}>
            {typedRows.map((r, i) => {
              const on = invites.has(r.contactId);
              return (
                <button key={r.contactId} type="button" onClick={() => onToggleInvite(r)} aria-pressed={on} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 4px', border: 0, borderTop: i === 0 && !rows.length ? 0 : '1px solid #1F1F1F', background: 'transparent', color: '#fff', fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer', width: '100%', boxSizing: 'border-box' }}>
                  <InitialsDisc initials="#" on={on} />
                  <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{r.name}</span>
                    <span style={{ fontSize: 10, letterSpacing: '.08em', color: '#6B6B6B' }}>{maskNumber(r.e164)} · TYPED IN</span>
                  </span>
                  <CheckBox on={on} />
                </button>
              );
            })}
          </div>
        )}
        {(!rows.length || source === 'none' || status !== 'done') && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, height: 50, padding: '0 6px 0 20px', borderRadius: 999, background: '#000', boxShadow: 'inset 0 0 0 1px #2A2A2A' }}>
            <input
              value={typed}
              onChange={(e) => onTyped(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onAddTyped(); } }}
              inputMode="tel"
              autoComplete="tel"
              placeholder="Type a phone number"
              aria-label="Phone number"
              className="pe-in"
              data-testid="typed-number"
              style={{ flex: 1, minWidth: 0, height: '100%', border: 0, background: 'none', boxShadow: 'none', outline: 'none', fontFamily: 'inherit', fontSize: 14, color: '#fff' }}
            />
            <MiniPill bg="#fff" fg="#000" hot height={38} onClick={onAddTyped} data-testid="add-number">+ ADD</MiniPill>
          </span>
        )}
        <span style={{ fontSize: 11, lineHeight: 1.5, color: '#6B6B6B' }}>Portal remembers you brought them.</span>
      </SectionCard>
    </>
  );
}

/** STEP 3 · WHAT ARE YOU HERE TO FIND? — the chips (+ your own) and the one line. */
export function StepHereToFind({ finds, custom, onToggleFind, draft, onDraft, onAddDraft, looking, onLooking }) {
  const love = finds.includes(LOVE_CHIP);
  const chips = [...HERE_TO_FIND, ...custom.filter((c) => !HERE_TO_FIND.includes(c))];
  return (
    <>
      <SectionCard gap={14} data-testid="finds-card">
        <SectionHead title="HERE TO FIND" right={<span style={{ color: '#6B6B6B', whiteSpace: 'nowrap', flexShrink: 0 }}>{finds.length} PICKED</span>} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {chips.map((label) => {
            const on = finds.includes(label);
            const isLove = label === LOVE_CHIP;
            return (
              <button
                key={label}
                type="button"
                onClick={() => onToggleFind(label)}
                aria-pressed={on}
                data-testid="find-chip"
                style={{ minHeight: 38, padding: '8px 16px', border: 0, borderRadius: 999, background: on ? (isLove ? '#FFCCE6' : '#fff') : '#161616', boxShadow: `inset 0 0 0 1px ${on ? 'transparent' : '#2A2A2A'}`, color: on ? '#000' : '#fff', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', cursor: 'pointer', transition: 'background-color .2s, color .2s' }}
              >
                {label}
              </button>
            );
          })}
        </div>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, height: 42, padding: '0 6px 0 18px', borderRadius: 999, background: '#000', boxShadow: 'inset 0 0 0 1px #2A2A2A' }}>
          <input
            value={draft}
            onChange={(e) => onDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onAddDraft(); } }}
            placeholder="Write your own…"
            maxLength={40}
            aria-label="Write your own"
            className="pe-in"
            data-testid="find-draft"
            style={{ flex: 1, minWidth: 0, height: '100%', border: 0, background: 'none', boxShadow: 'none', outline: 'none', fontFamily: 'inherit', fontSize: 13, color: '#fff' }}
          />
          <MiniPill height={32} onClick={onAddDraft} data-testid="find-add">+ ADD</MiniPill>
        </span>
        <span data-testid="love-note" style={{ fontSize: 11, lineHeight: 1.55, color: love ? '#FFCCE6' : '#6B6B6B', transition: 'color .2s' }}>
          <span style={{ fontWeight: 800, letterSpacing: '.12em' }}>LOVE</span> is shown to nobody. It only matches two people who both picked it.
        </span>
      </SectionCard>

      <SectionCard gap={12} data-testid="looking-card">
        <SectionHead title="RIGHT NOW I’M LOOKING FOR" right={<span style={{ color: '#6B6B6B' }}>OPTIONAL</span>} />
        <span style={HELP}>One line. The fastest way to get an introduction. A friend who knows everyone reads this first.</span>
        <input
          value={looking}
          onChange={(e) => onLooking(e.target.value.slice(0, LOOKING_MAX))}
          maxLength={LOOKING_MAX}
          placeholder="A DJ for Dec 12"
          aria-label="Right now I’m looking for"
          className="pe-in"
          data-testid="looking-input"
          style={{ ...pill(), height: 52 }}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ ...LABEL, color: '#6B6B6B' }}>LINES THAT WORK · TAP TO USE</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {LOOKING_EXAMPLES.map((t) => (
              <button key={t} type="button" onClick={() => onLooking(t)} className="pe-vibe" style={{ minHeight: 34, padding: '6px 14px', border: 0, borderRadius: 999, background: '#1A1A1A', color: '#BDBDBD', fontFamily: 'inherit', fontSize: 12, cursor: 'pointer', transition: 'background-color .2s, color .2s' }}>{t}</button>
            ))}
          </div>
        </div>
      </SectionCard>
    </>
  );
}

/** STEP 4 · THE BASICS — the optional photo, the name, the vibe check. */
export function StepYourFace({ photo, uploading, tried, onPick, onRemove, name, onName, vibe, onVibe }) {
  const missingName = tried && !name.trim();
  return (
    <>
      <SectionCard gap={16} style={{ padding: 22 }} data-testid="photo-card">
        <SectionHead
          title="YOUR PHOTO"
          right={<span data-testid="photo-meta" style={{ color: photo ? CYAN : '#6B6B6B', whiteSpace: 'nowrap' }}>{photo ? '✓ LOOKS LIKE YOU' : 'OPTIONAL'}</span>}
        />
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 18 }}>
          <button
            type="button"
            onClick={onPick}
            aria-label={photo ? 'Change your photo' : 'Add a photo'}
            data-testid="photo-circle"
            className="pr-ring-anim"
            style={{ flexShrink: 0, position: 'relative', width: 124, height: 124, padding: 0, border: '3px solid transparent', borderRadius: '50%', ...irisRing('#000'), cursor: 'pointer', boxSizing: 'border-box' }}
          >
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden', background: '#000' }}>
              {photo
                ? <img src={photo.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: uploading ? 0.5 : 1, transition: 'opacity .2s' }} />
                : (
                  <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, color: '#fff' }}>
                    {uploading ? null : Icon.plus(22)}
                    <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.14em' }}>{uploading ? 'UPLOADING…' : 'ADD A PHOTO'}</span>
                  </span>
                )}
            </span>
          </button>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, flex: 1, minWidth: 180 }}>
            <MiniPill bg="#fff" fg="#000" hot onClick={onPick} disabled={uploading} data-testid="photo-pick">{uploading ? 'UPLOADING…' : photo ? 'CHANGE PHOTO' : 'CHOOSE A PHOTO'}</MiniPill>
            {photo && onRemove && <MiniPill onClick={onRemove} disabled={uploading} data-testid="photo-remove">REMOVE</MiniPill>}
          </div>
        </div>
      </SectionCard>

      <SectionCard gap={16} style={{ padding: 22 }} data-testid="identity-card">
        <PillField label="NAME">
          <input
            value={name}
            onChange={(e) => onName(e.target.value)}
            maxLength={60}
            placeholder="What people call you"
            autoComplete="name"
            className="pe-in"
            data-testid="name-input"
            style={{ ...pill(missingName ? PINK : '#2A2A2A'), height: 52, padding: '0 22px', fontSize: 15, fontWeight: 700 }}
          />
        </PillField>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
          <span style={{ ...LABEL, display: 'flex', justifyContent: 'space-between', gap: 10 }}>VIBE CHECK <span style={{ color: '#6B6B6B' }}>{vibe.length} / {VIBE_MAX}</span></span>
          <VibeField value={vibe} onChange={onVibe} height={56} fontSize={18} examplesLabel="ONE SENTENCE IN YOUR VOICE · TAP TO USE" />
        </label>
      </SectionCard>
      <span style={{ fontSize: 11, lineHeight: 1.55, color: '#6B6B6B', padding: '0 6px' }}>Music, strengths and the rest come later, in your Portal Editor.</span>
    </>
  );
}
